import { attachRegionsHover, attachPolarSliceHover, attachIndexXHover, attachNearestHover } from '../core/interaction';
import { registerDashboard } from '../../registryCore';
import { createWidgetGroup, num, setState } from '../../helpers';
import { getActiveDashboard } from '../../theme';
import { squarify, flattenHierarchy } from '../core/treemap';
import { Arc } from '../../../shapes/index';
var SAMPLE_TREE = {
    name: 'root',
    children: [
        { name: 'A', value: 40 },
        { name: 'B', value: 30 },
        { name: 'C', value: 20 },
        { name: 'D', value: 10 },
    ],
};
registerDashboard('treemap', function (props, app) {
    var _a, _b;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 200);
    var root = (_a = props.data) !== null && _a !== void 0 ? _a : SAMPLE_TREE;
    var nodes = (_b = root.children) !== null && _b !== void 0 ? _b : [root];
    var group = createWidgetGroup(app, 'treemap', props);
    var rects = squarify(nodes, 0, 0, width, height);
    var regions = rects.map(function (r) {
        var _a;
        return ({
            x: r.x,
            y: r.y,
            width: r.width - 1,
            height: r.height - 1,
            label: "".concat(r.name, ": ").concat((_a = r.value) !== null && _a !== void 0 ? _a : ''),
            payload: { name: r.name, value: r.value },
        });
    });
    rects.forEach(function (r, i) {
        group.add(app.rect({
            x: r.x,
            y: r.y,
            width: r.width - 1,
            height: r.height - 1,
            fill: getActiveDashboard().series[i % getActiveDashboard().series.length],
            stroke: getActiveDashboard().pieStroke,
            strokeWidth: 1,
            listening: false,
        }), app.text({
            text: r.name,
            x: r.x + 4,
            y: r.y + 4,
            fontSize: 10,
            fill: getActiveDashboard().text,
            listening: false,
        }));
    });
    attachRegionsHover(app, group, props, width, height, regions);
    setState(group, { width: width, height: height, data: root });
    return group;
});
registerDashboard('sunburstChart', function (props, app) {
    var _a, _b;
    var size = num(props, 'size', 200);
    var root = (_a = props.data) !== null && _a !== void 0 ? _a : SAMPLE_TREE;
    var group = createWidgetGroup(app, 'sunburstChart', props);
    var cx = size / 2;
    var children = (_b = root.children) !== null && _b !== void 0 ? _b : [root];
    var total = children.reduce(function (a, c) { var _a; return a + ((_a = c.value) !== null && _a !== void 0 ? _a : 1); }, 0);
    var angle = -Math.PI / 2;
    children.forEach(function (c, i) {
        var _a;
        var sweep = (((_a = c.value) !== null && _a !== void 0 ? _a : 1) / total) * Math.PI * 2;
        group.add(new Arc({
            x: cx - size / 2 + 10,
            y: cx - size / 2 + 10,
            innerRadius: size * 0.2,
            radius: size / 2 - 10,
            startAngle: angle,
            endAngle: angle + sweep,
            fill: getActiveDashboard().series[i % getActiveDashboard().series.length],
            stroke: getActiveDashboard().pieStroke,
            strokeWidth: 1,
            listening: false,
        }));
        angle += sweep;
    });
    var sliceData = children.map(function (c) { var _a; return (_a = c.value) !== null && _a !== void 0 ? _a : 1; });
    attachPolarSliceHover(app, group, props, size, sliceData, children.map(function (c) { var _a; return "".concat(c.name, ": ").concat((_a = c.value) !== null && _a !== void 0 ? _a : 1); }));
    setState(group, { size: size, data: root });
    return group;
});
registerDashboard('treeChart', function (props, app) {
    var _a;
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 200);
    var root = (_a = props.data) !== null && _a !== void 0 ? _a : {
        name: 'root',
        children: [
            { name: 'A', children: [{ name: 'A1' }, { name: 'A2' }] },
            { name: 'B', children: [{ name: 'B1' }] },
        ],
    };
    var group = createWidgetGroup(app, 'treeChart', props);
    group.add(app.rect({ width: width, height: height, fill: getActiveDashboard().chartBg, listening: true }));
    var leaves = flattenHierarchy(root);
    leaves.forEach(function (n, i) {
        var x = 20 + (i % 4) * 70;
        var y = 30 + Math.floor(i / 4) * 50;
        group.add(app.circle({ x: x - 8, y: y - 8, radius: 8, fill: getActiveDashboard().primary, listening: false }), app.text({ text: n.name, x: x + 12, y: y - 6, fontSize: 11, fill: getActiveDashboard().text, listening: false }));
    });
    attachNearestHover(app, group, props, { x: 0, y: 0, width: width, height: height }, leaves.map(function (n, i) {
        var _a;
        var x = 20 + (i % 4) * 70;
        var y = 30 + Math.floor(i / 4) * 50;
        return { x: x, y: y, label: (_a = n.name) !== null && _a !== void 0 ? _a : 'node' };
    }));
    setState(group, { width: width, height: height, data: root });
    return group;
});
registerDashboard('dendrogramChart', function (props, app) {
    var width = num(props, 'width', 300);
    var height = num(props, 'height', 200);
    var group = createWidgetGroup(app, 'dendrogramChart', props);
    group.add(app.rect({ width: width, height: height, fill: getActiveDashboard().chartBg, listening: true }));
    var nodes = ['A', 'B', 'C', 'D', 'E'];
    var step = width / nodes.length;
    nodes.forEach(function (n, i) {
        var x = step * i + step / 2;
        group.add(app.line({ x: x, y: height - 30, x2: 0, y2: -80, stroke: getActiveDashboard().chartGrid, strokeWidth: 1, listening: false }), app.text({ text: n, x: x - 6, y: height - 12, fontSize: 10, fill: getActiveDashboard().text, listening: false }));
    });
    group.add(app.line({ x: step / 2, y: height - 110, x2: width - step, y2: 0, stroke: getActiveDashboard().chartLine, strokeWidth: 2, listening: false }));
    attachIndexXHover(app, group, props, { plotX: 0, plotY: 0, plotWidth: width, plotHeight: height }, nodes.length, function (i) { return nodes[i]; });
    setState(group, { width: width, height: height });
    return group;
});
